import { getApiBaseUrl } from '@/config/env';

/**
 * Normalizes stored file paths from the API (bare filenames, missing prefixes, legacy paths).
 */
export function normalizeStoredPath(path: string): string | undefined {
  const trimmed = path.trim();
  if (!trimmed) {
    return undefined;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
    const apiIdx = trimmed.indexOf('/api/');
    if (apiIdx >= 0) {
      return trimmed.slice(apiIdx + 4);
    }
    return trimmed;
  }
  const withoutQuery = trimmed.split('?')[0];
  if (
    withoutQuery.startsWith('/images/') ||
    withoutQuery.startsWith('/documents/') ||
    withoutQuery.startsWith('/files/')
  ) {
    return withoutQuery;
  }
  if (withoutQuery.startsWith('images/') || withoutQuery.startsWith('documents/') || withoutQuery.startsWith('files/')) {
    return `/${withoutQuery}`;
  }
  if (withoutQuery.startsWith('/')) {
    const name = withoutQuery.slice(1);
    return name.includes('/') ? withoutQuery : `/documents/${name}`;
  }
  return `/documents/${withoutQuery}`;
}

/**
 * Resolves API-relative file URLs for use in img/src/href (includes API base path).
 */
export function resolveFileUrl(path: string | undefined | null): string | undefined {
  const normalized = path ? normalizeStoredPath(path) : undefined;
  if (!normalized) {
    return undefined;
  }
  if (normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('data:')) {
    return normalized;
  }
  const base = getApiBaseUrl();
  return normalized.startsWith('/') ? `${base}${normalized}` : `${base}/${normalized}`;
}

/**
 * Authenticated content endpoint — reliable preview/download with session cookies.
 */
export function resolveFileContentUrl(path: string | undefined | null, disposition: 'inline' | 'attachment' = 'inline'): string | undefined {
  const normalized = path ? normalizeStoredPath(path) : undefined;
  if (!normalized || normalized.startsWith('http')) {
    return resolveFileUrl(path);
  }
  const base = getApiBaseUrl();
  const params = new URLSearchParams({
    path: normalized,
    disposition,
  });
  return `${base}/v1/files/content?${params.toString()}`;
}

/**
 * Profile photos are stored server-side; resolve a stable URL that works in any browser.
 * Uses GET /v1/public/profile-pictures/{userUuid} for uploaded and OAuth photos.
 */
export function resolveProfileImageUrl(
  path: string | null | undefined,
  userUuid?: string | null,
): string | undefined {
  const trimmed = path?.trim() ?? '';
  if (!trimmed && !userUuid) {
    return undefined;
  }
  if (trimmed.startsWith('data:')) {
    return trimmed;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  if (userUuid && trimmed) {
    return `${getApiBaseUrl()}/v1/public/profile-pictures/${userUuid}`;
  }
  return trimmed ? resolveFileUrl(trimmed) : undefined;
}
